import { Button, Spin } from 'antd';
import classNames from 'classnames';
import { orderBy } from 'lodash';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TRENDING_FLAG, sortAttAsc, sortAttDesc, sortAttName, sortAttPrice } from '@common/constants';
import { CookingApplianceOptions, SortBy } from '@common/enums';
import { ApplianceModel, ApplianceType } from '@common/models';
import { isCookingApplianceOptionEnabled } from '@common/utils';
import { Filters, SectionSubtitle, SortByMenu } from '@core/components';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    resetSelectedCookingApplianceId,
    resetSelectedCookingApplianceOption,
    selectCookingApplianceSortBy,
    selectSelectedCookingApplianceOption,
    updateCookingApplianceFilters,
    updateCookingApplianceSortBy,
    updateSelectedCookingApplianceOption,
} from '@redux/slices/cookingApplianceSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import SupplyIcon from '../../../../../../public/assets/icons/supply.svg';
import TrashIcon from '../../../../../../public/assets/icons/trash.svg';
import useAppliance from '../appliances/useAppliance';
import {
    hasInStoreItems,
    hasProgramItems,
    isApplianceSourceInStore,
    isApplianceSourceProgram,
} from './CookingAppliancesSection.utils';
import CookingApplianceItem from './components/CookingApplianceItem';
import useCookingAppliance from './hooks/useCookingAppliance';
import useFilteredCookingAppliances from './hooks/useFilteredCookingAppliances';

import './CookingAppliancesSection.scss';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function CookingAppliance(): JSX.Element | null {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isFilterExpanded, setFilterExpanded] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    const { customerSuppliedCookingAppliances, isFetchingCookingAppliances } = useCookingAppliance();
    const { filteredCookingAppliances, cookingApplianceFilters } = useFilteredCookingAppliances();
    const { updateActiveDrawingShape, lastActiveDrawingShape, areThereShapesInTheDrawingArea } =
        useContext(DrawingShapeContext);
    const { applianceTypesInDrawingArea, deleteAllAppliancesByType } = useAppliance();
    const { retailerFileSettings } = useRetailerSettings();

    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { selectedValue: selectedStore } = useSelectedStore();

    const zipcode = useAppSelector(selectZipcode);
    const cookingApplianceSortBy = useAppSelector(selectCookingApplianceSortBy);
    const selectedCookingApplianceOption = useAppSelector(selectSelectedCookingApplianceOption);

    const sortCookingAppliances = (cookingAppliances: Array<ApplianceModel>): Array<ApplianceModel> => {
        switch (cookingApplianceSortBy) {
            case SortBy.HighToLow:
                return orderBy(cookingAppliances, [sortAttPrice], [sortAttDesc]);
            case SortBy.LowToHigh:
                return orderBy(cookingAppliances, [sortAttPrice], [sortAttAsc]);
            case SortBy.NameAZ:
                return orderBy(cookingAppliances, [sortAttName], [sortAttAsc]);
            case SortBy.NameZA:
                return orderBy(cookingAppliances, [sortAttName], [sortAttDesc]);
            case SortBy.TrendingNow: {
                const onTrendCookingAppliances = cookingAppliances.filter((cookingAppliance: ApplianceModel) =>
                    cookingAppliance.features.find((item) => item.name.match(TRENDING_FLAG))
                );

                const otherCookingAppliances = cookingAppliances.filter(
                    (cookingAppliance: ApplianceModel) =>
                        !cookingAppliance.features.find((item) => item.name.match(TRENDING_FLAG)) && cookingAppliance
                );

                return [...onTrendCookingAppliances, ...otherCookingAppliances];
            }
            default:
                return orderBy(cookingAppliances, [sortAttName], [sortAttAsc]);
        }
    };

    const onOptionSelected = (cookingApplianceOption: CookingApplianceOptions) => {
        if (selectedCookingApplianceOption === cookingApplianceOption) {
            dispatch(resetSelectedCookingApplianceOption());
            return;
        }

        dispatch(updateSelectedCookingApplianceOption(cookingApplianceOption));
        dispatch(resetSelectedCookingApplianceId());
        if (cookingApplianceOption === CookingApplianceOptions.NONE) {
            if (applianceTypesInDrawingArea([ApplianceType.COOKTOP, ApplianceType.RANGE])) {
                setIsModalVisible(true);
            }
        } else {
            scrollToLoadedCookingAppliances();
        }
    };

    // TODO: Fix these functions and deduplicate them to be a generic implementation
    const scrollToLoadedCookingAppliances = () => {
        const estimatePageElm = document.getElementById('ScrollableArea__bottom-section');
        const drawingArea = document.getElementsByClassName('drawing-area')[0];

        if (!estimatePageElm || !drawingArea || !ref || !ref.current) {
            return;
        }

        estimatePageElm.scrollTo({
            top: estimatePageElm.scrollTop + ref.current.getBoundingClientRect().bottom - drawingArea.clientHeight,
        });
    };

    const onConfirmFromConfirmationModal = () => {
        deleteAllAppliancesByType([ApplianceType.COOKTOP, ApplianceType.RANGE]);
        setIsModalVisible(false);
        updateActiveDrawingShape(lastActiveDrawingShape);
    };

    const onCancelFromConfirmationModal = () => {
        dispatch(resetSelectedCookingApplianceOption());
        setIsModalVisible(false);
    };

    const cookingAppliances =
        // eslint-disable-next-line no-nested-ternary
        selectedCookingApplianceOption === CookingApplianceOptions.BROWSE
            ? filteredCookingAppliances
            : selectedCookingApplianceOption === CookingApplianceOptions.SUPPLY
            ? customerSuppliedCookingAppliances
            : [];

    const areCookingAppliancesAvailable = Boolean(
        zipcode && selectedStore && selectedMaterial && areThereShapesInTheDrawingArea
    );
    if (!areCookingAppliancesAvailable) {
        return null;
    }

    return (
        <>
            <SectionSubtitle
                name={t('SCROLLABLE_AREA.COOKINGAPPLIANCE.TITLE')}
                description={t('SCROLLABLE_AREA.COOKINGAPPLIANCE.DESCRIPTION')}
                showLine
            />
            <div ref={ref} className="appliance-options cooking-appliance">
                {Object.entries(CookingApplianceOptions).map(
                    ([cookingApplianceOption, cookingApplianceOptionValue], index) =>
                        isCookingApplianceOptionEnabled(retailerFileSettings, cookingApplianceOptionValue) ? (
                            <Button
                                key={index}
                                className={classNames({
                                    selected: cookingApplianceOptionValue === selectedCookingApplianceOption,
                                })}
                                onClick={() => onOptionSelected(cookingApplianceOptionValue)}
                            >
                                {t(`SCROLLABLE_AREA.COOKINGAPPLIANCE.OPTIONS.${cookingApplianceOption}`)}
                            </Button>
                        ) : null
                )}
            </div>

            {selectedCookingApplianceOption && selectedCookingApplianceOption !== CookingApplianceOptions.NONE && (
                <div className="title title-with-sort">
                    {t(`SCROLLABLE_AREA.COOKINGAPPLIANCE.SUPPLIED_COOKING_APPLIANCE_INFO`)}
                    <SortByMenu
                        sortBy={cookingApplianceSortBy}
                        setSortBy={(newSortby) => {
                            dispatch(updateCookingApplianceSortBy(newSortby));
                        }}
                        options={[SortBy.TrendingNow, SortBy.LowToHigh, SortBy.HighToLow, SortBy.NameAZ, SortBy.NameZA]}
                    />
                </div>
            )}
            {isFetchingCookingAppliances ? (
                <Spin style={{ fontSize: 24 }} />
            ) : (
                <div className={`cooking-appliance-card-wrapper ${isFilterExpanded ? 'row-direction' : ''}`}>
                    {selectedCookingApplianceOption === CookingApplianceOptions.BROWSE && (
                        <Filters
                            isFilterExpanded={isFilterExpanded}
                            setFilterExpanded={setFilterExpanded}
                            filterOptions={cookingApplianceFilters}
                            updateFilters={(filters) => {
                                dispatch(updateCookingApplianceFilters(filters));
                            }}
                        />
                    )}
                    <div className="cooking-appliance-group-wrapper">
                        {selectedCookingApplianceOption === CookingApplianceOptions.BROWSE ? (
                            <>
                                {hasProgramItems(cookingAppliances) ? (
                                    <div className="card-appliance">
                                        {sortCookingAppliances(cookingAppliances.filter(isApplianceSourceProgram)).map(
                                            (cookingAppliance, id) => (
                                                <CookingApplianceItem key={id} cookingAppliance={cookingAppliance} />
                                            )
                                        )}
                                    </div>
                                ) : null}

                                {hasInStoreItems(cookingAppliances) ? (
                                    <div className="card-appliance">
                                        {sortCookingAppliances(cookingAppliances.filter(isApplianceSourceInStore)).map(
                                            (cookingAppliance, id) => (
                                                <CookingApplianceItem key={id} cookingAppliance={cookingAppliance} />
                                            )
                                        )}
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <div className="card-appliance">
                                {sortCookingAppliances(cookingAppliances).map(
                                    (cookingAppliance: ApplianceModel, id: number) => (
                                        <CookingApplianceItem key={id} cookingAppliance={cookingAppliance} />
                                    )
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <ConfirmationDialog
                visible={isModalVisible}
                onCancel={onCancelFromConfirmationModal}
                onConfirm={onConfirmFromConfirmationModal}
                icon={
                    selectedCookingApplianceOption === CookingApplianceOptions.SUPPLY ? <SupplyIcon /> : <TrashIcon />
                }
                title={
                    selectedCookingApplianceOption === CookingApplianceOptions.SUPPLY
                        ? t('SCROLLABLE_AREA.COOKINGAPPLIANCE.SUPPLY_COOKING_APPLIANCE_TITLE')
                        : t('SCROLLABLE_AREA.COOKINGAPPLIANCE.NO_COOKING_APPLIANCE_IN_SPACE_TITLE')
                }
                content={
                    selectedCookingApplianceOption === CookingApplianceOptions.SUPPLY
                        ? t('SCROLLABLE_AREA.COOKINGAPPLIANCE.SUPPLY_COOKING_APPLIANCE_DESCRIPTION')
                        : t('SCROLLABLE_AREA.COOKINGAPPLIANCE.NO_COOKING_APPLIANCE_IN_SPACE_DESCRIPTION')
                }
            />
        </>
    );
}
